import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import type { ReferringPerson, RelationshipType, ReachDifficulty, EmployerWithReferringPeople } from '@/types/referrer'
import { useRouter } from 'vue-router'

export const useReferrersStore = defineStore('referrers', () => {
  // Data state
  const isLoading = ref(false)
  const isLoadingMore = ref(false)
  const initialized = ref(false)
  const referrers = ref<ReferringPerson[]>([])
  const error = ref<string | null>(null)
  const referrersGroupedByCompany = ref<EmployerWithReferringPeople[]>([])
  const historicalStats = ref<{
    totalReferrers: number
    activeReferrers: number
    timestamp: string
  }[]>([])

  // Pagination state
  const nextPageUrl = ref<string | null>(null)
  const hasMore = ref(false)
  const totalCompaniesCount = ref(0)
  const totalReferrersCount = ref(0)
  const activeReferrersCount = ref(0)
  
  // Sorting state
  const currentSort = ref<string>('')
  
  // Filter state
  const filterActive = ref<boolean | null>(null)
  const filterEmployerIds = ref<number[]>([])
  const filterRelationshipType = ref<RelationshipType | null>(null)
  const filterReachability = ref<ReachDifficulty | null>(null)
  const searchQuery = ref<string>('')

  // Computed statistics
  const totalReferrers = computed(() => {
    return referrersGroupedByCompany.value.reduce((total, company) => {
      return total + company.referring_people.length
    }, 0)
  })

  const activeReferrers = computed(() => {
    return referrersGroupedByCompany.value.reduce((total, company) => {
      return total + company.referring_people.filter(r => r.is_active).length
    }, 0)
  })

  const growthRate = computed(() => {
    if (historicalStats.value.length < 2) return 0
    
    const current = totalReferrers.value
    const previous = historicalStats.value[historicalStats.value.length - 1].totalReferrers
    
    if (previous === 0) return 0
    return Math.round(((current - previous) / previous) * 100)
  })

  /**
   * Build the grouped_by_company URL with all active filters as query params
   */
  function buildGroupedByCompanyUrl(): string {
    const baseUrl = API_ROUTES.GET_REFERRERS_GROUPED_BY_COMPANY
    const queryParams = new URLSearchParams()

    if (filterActive.value !== null) {
      queryParams.append('is_active', filterActive.value.toString())
    }
    if (filterEmployerIds.value.length > 0) {
      queryParams.append('employer_ids', filterEmployerIds.value.join(','))
    }
    if (filterRelationshipType.value) {
      queryParams.append('relationship_type', filterRelationshipType.value)
    }
    if (filterReachability.value) {
      queryParams.append('reachability', filterReachability.value)
    }
    if (searchQuery.value) {
      queryParams.append('search', searchQuery.value)
    }

    const queryString = queryParams.toString()
    return queryString ? `${baseUrl}?${queryString}` : baseUrl
  }


  /**
   * Fetch referrers with optional filtering and sorting
   * @param params - Optional parameters like specific employer ID
   */
  async function fetchReferrers(params: { employerId?: number } = {}) {
    isLoading.value = true
    error.value = null
    
    try {
      const baseUrl = API_ROUTES.GET_REFERRERS
      const queryParams = new URLSearchParams()
      
      // Add employer ID from params if provided
      if (params.employerId) {
        queryParams.append('employer_id', params.employerId.toString())
      }
      
      // Add sorting if present
      if (currentSort.value) {
        queryParams.append('ordering', currentSort.value)
      }
      
      // Add active filter if set
      if (filterActive.value !== null) {
        queryParams.append('is_active', filterActive.value.toString())
      }
      
      // Add employer IDs filter if any
      if (filterEmployerIds.value.length > 0) {
        queryParams.append('employer_ids', filterEmployerIds.value.join(','))
      }
      
      // Add relationship type filter if set
      if (filterRelationshipType.value) {
        queryParams.append('relationship_type', filterRelationshipType.value)
      }
      
      // Add reachability filter if set
      if (filterReachability.value) {
        queryParams.append('reachability', filterReachability.value)
      }
      
      // Add search query if present
      if (searchQuery.value) {
        queryParams.append('search', searchQuery.value)
      }
      
      const queryString = queryParams.toString()
      const url = queryString ? `${baseUrl}?${queryString}` : baseUrl
      
      const response = await api.get(url)
      referrers.value = response.data
    } catch (err) {
      error.value = 'Failed to fetch referrers'
      console.error('Error fetching referrers:', err)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Fetch referrers grouped by company from the server (page 1), applying all
   * active filters as query params. Replaces any previously loaded data.
   */
  async function fetchReferrersGroupedByCompany(_forceRefresh = false) {
    isLoading.value = true
    error.value = null

    try {
      const url = buildGroupedByCompanyUrl()
      const response = await api.get(url)
      const data = response.data

      referrersGroupedByCompany.value = data.results
      nextPageUrl.value = data.next
      hasMore.value = !!data.next
      totalCompaniesCount.value = data.count
      totalReferrersCount.value = data.total_referrers ?? 0
      activeReferrersCount.value = data.active_referrers ?? 0
      initialized.value = true
    } catch (err) {
      error.value = 'Failed to fetch referrers grouped by company'
      console.error('Error fetching referrers grouped by company:', err)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Load the next page of company groups and append them to the existing list.
   */
  async function fetchNextPage() {
    if (!nextPageUrl.value || isLoadingMore.value) return
    isLoadingMore.value = true

    try {
      // Use only the query string from the backend's next URL (which may be http://)
      // and attach it to the known HTTPS route, avoiding both mixed-content and double-path issues.
      const nextSearch = new URL(nextPageUrl.value).search
      const response = await api.get(API_ROUTES.GET_REFERRERS_GROUPED_BY_COMPANY + nextSearch)
      const data = response.data

      referrersGroupedByCompany.value = [
        ...referrersGroupedByCompany.value,
        ...data.results,
      ]
      nextPageUrl.value = data.next
      hasMore.value = !!data.next
      totalCompaniesCount.value = data.count
      totalReferrersCount.value = data.total_referrers ?? totalReferrersCount.value
      activeReferrersCount.value = data.active_referrers ?? activeReferrersCount.value
    } catch (err) {
      console.error('Error fetching next page of referrers:', err)
    } finally {
      isLoadingMore.value = false
    }
  }

  /**
   * Set a referrer as the default for a company
   * @param employerId - ID of the employer/company
   * @param referrerId - ID of the referrer to set as default, or null to clear the default
   * @returns - Promise resolving to the API response
   */
  async function setDefaultReferrer(employerId: number | string, referrerId: number | string | null) {
    error.value = null
    isLoading.value = true
    
    try {
      const url = API_ROUTES.SET_DEFAULT_REFERRER(employerId)
      const data = { referring_person_id: referrerId }
      
      const response = await api.post(url, data)
      
      // Update local data stores to reflect the change
      updateDefaultReferrerInLocalData(Number(employerId), referrerId)
      
      return response.data
    } catch (err) {
      error.value = 'Failed to set default referrer'
      console.error('Error setting default referrer:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }
  
  /**
   * Update the local data to reflect a change in default referrer
   */
  function updateDefaultReferrerInLocalData(employerId: number, referrerId: number | string | null) {
    const company = referrersGroupedByCompany.value.find(c => c.id === employerId)
    if (company) {
      company.default_referrer = referrerId === null ? undefined : Number(referrerId)
    }
  }

  /**
   * Update a referrer's information
   * @param id - Referrer ID
   * @param data - Data to update
   * @param partial - Whether to use PATCH (true) or PUT (false)
   * @returns - The updated referrer
   */
  async function updateReferrer(
    id: number | string, 
    data: Partial<ReferringPerson>, 
    partial: boolean = true
  ) {
    // Don't clear global error or set loading for individual updates
    // This prevents the entire table from being replaced by error state
    
    try {
      const url = partial 
        ? API_ROUTES.PATCH_REFERRER(id) 
        : API_ROUTES.UPDATE_REFERRER(id)
      
      const method = partial ? 'patch' : 'put'
      const response = await api[method](url, data)
      
      // Update the local referrer data
      const index = referrers.value.findIndex(r => r.id === id)
      if (index !== -1) {
        referrers.value[index] = response.data
      }
      
      // Also update the grouped data if it exists
      if (referrersGroupedByCompany.value.length > 0) {
        updateReferrerInGroupedData(id, response.data)
      }
      
      return response.data
    } catch (err) {
      // Don't set global error state - let the component handle it
      console.error('Error updating referrer:', err)
      throw err
    }
  }

  /**
   * Update a referrer in the grouped company data
   */
  function updateReferrerInGroupedData(id: number | string, updatedReferrer: ReferringPerson) {
    for (const company of referrersGroupedByCompany.value) {
      const referrerIndex = company.referring_people.findIndex(r => r.id === id)
      if (referrerIndex !== -1) {
        company.referring_people[referrerIndex] = updatedReferrer
      }
    }
  }

  /**
   * Toggle sort order for a field
   * @param field - The field to sort by
   * @param router - Vue router instance for updating URL
   */
  function updateSort(field: string, router?: ReturnType<typeof useRouter>) {
    // Toggle between ascending and descending only
    if (currentSort.value === field) {
      currentSort.value = '-' + field
    } else if (currentSort.value === '-' + field) {
      currentSort.value = field
    } else {
      currentSort.value = field
    }

    // Update URL query params if router is provided
    if (router) {
      const query = { 
        ...router.currentRoute.value.query, 
        ordering: currentSort.value || undefined
      }
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      router.replace({ query })
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchReferrers()
  }

  /**
   * Set filter for active/inactive referrers
   * @param isActive - true for active, false for inactive, null for both
   * @param router - Vue router instance for updating URL
   */
  function setActiveFilter(isActive: boolean | null, router?: ReturnType<typeof useRouter>) {
    filterActive.value = isActive
    
    if (router) {
      const query = { 
        ...router.currentRoute.value.query,
        is_active: isActive === null ? undefined : isActive.toString()
      }
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      router.replace({ query })
    }
    
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchReferrers()
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchReferrersGroupedByCompany()
  }

  /**
   * Filter referrers by associated employer IDs
   * @param employerIds - Array of employer IDs to filter by
   * @param router - Vue router instance for updating URL
   */
  function setEmployerIdsFilter(employerIds: number[], router?: ReturnType<typeof useRouter>) {
    filterEmployerIds.value = employerIds
    
    if (router) {
      const query = { 
        ...router.currentRoute.value.query,
        employer_ids: employerIds.length > 0 ? employerIds.join(',') : undefined
      }
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      router.replace({ query })
    }
    
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchReferrers()
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchReferrersGroupedByCompany()
  }

  /**
   * Filter referrers by relationship type
   * @param relationshipType - Relationship type to filter by
   * @param router - Vue router instance for updating URL
   */
  function setRelationshipTypeFilter(relationshipType: RelationshipType | null, router?: ReturnType<typeof useRouter>) {
    filterRelationshipType.value = relationshipType
    
    if (router) {
      const query = { 
        ...router.currentRoute.value.query,
        relationship_type: relationshipType || undefined
      }
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      router.replace({ query })
    }
    
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchReferrers()
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchReferrersGroupedByCompany()
  }

  /**
   * Filter referrers by reachability
   * @param reachability - Reachability level to filter by
   * @param router - Vue router instance for updating URL
   */
  function setReachabilityFilter(reachability: ReachDifficulty | null, router?: ReturnType<typeof useRouter>) {
    filterReachability.value = reachability
    
    if (router) {
      const query = { 
        ...router.currentRoute.value.query,
        reachability: reachability || undefined
      }
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      router.replace({ query })
    }
    
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchReferrers()
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchReferrersGroupedByCompany()
  }

  /**
   * Set the search query and update filters
   */
  function setSearchQuery(query: string, router?: ReturnType<typeof useRouter>) {
    searchQuery.value = query
    
    // Update URL if router is provided
    if (router) {
      const currentQuery = { ...router.currentRoute.value.query }
      if (query) {
        currentQuery.search = query
      } else {
        delete currentQuery.search
      }
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      router.replace({ query: currentQuery })
    }
    // The view's watcher on searchQuery drives the debounced grouped fetch.
    // For the flat referrers list, trigger a fetch directly.
    if (!initialized.value) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      fetchReferrers()
    }
  }

  /**
   * Clear all active filters
   * @param router - Vue router instance for updating URL
   */
  function clearFilters(router?: ReturnType<typeof useRouter>) {
    filterActive.value = null
    filterEmployerIds.value = []
    filterRelationshipType.value = null
    filterReachability.value = null
    searchQuery.value = ''
    
    if (router) {
      const { ordering } = router.currentRoute.value.query
      const query = { ordering }
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      router.replace({ query })
    }
    
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchReferrers()
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchReferrersGroupedByCompany()
  }

  /**
   * Create a new referrer
   * @param data - The referrer data to create
   * @returns Promise resolving to the created referrer
   */
  async function createReferrer(data: Partial<ReferringPerson>) {
    error.value = null
    isLoading.value = true
    
    try {
      const response = await api.post(API_ROUTES.GET_REFERRERS, data)
      
      // Add the new referrer to the local state
      referrers.value = [...referrers.value, response.data]

      // Refresh grouped data to include the newly created referrer
      await fetchReferrersGroupedByCompany()
      
      return response.data
    } catch (err) {
      error.value = 'Failed to create referrer'
      console.error('Error creating referrer:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  return {
    // State
    isLoading,
    isLoadingMore,
    initialized,
    referrers,
    error,
    currentSort,
    filterActive,
    filterEmployerIds,
    filterRelationshipType,
    filterReachability,
    searchQuery,
    referrersGroupedByCompany,
    // Pagination
    nextPageUrl,
    hasMore,
    totalCompaniesCount,
    totalReferrersCount,
    activeReferrersCount,
    
    // Computed
    totalReferrers,
    activeReferrers,
    growthRate,
    
    // Actions
    fetchReferrers,
    fetchReferrersGroupedByCompany,
    fetchNextPage,
    updateReferrer,
    updateSort,
    setActiveFilter,
    setEmployerIdsFilter,
    setRelationshipTypeFilter,
    setReachabilityFilter,
    setSearchQuery,
    clearFilters,
    setDefaultReferrer,
    createReferrer
  }
})
